import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package, Plus, Search, Pencil, Archive, Tag, TrendingUp,
  Wand2, Upload, Download,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat(undefined, { style: "currency", currency: "SGD" }).format(n || 0);
const emptyForm = {
  name: "", description: "", unit_price: "", cost_price: "",
  category: "", sku: "", include_gst: true, include_service_fee: true,
};
const CATEGORY_ANY = "__all__";
const CATEGORY_NONE = "__none__";

export default function Products() {
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(CATEGORY_ANY);
  const [showArchived, setShowArchived] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileRef = useRef(null);
  const qc = useQueryClient();

  const { data: products = [] } = useQuery({
    queryKey: ["products", q, showArchived, categoryFilter],
    queryFn: () => api.get("/products", { params: {
      search: q || undefined,
      include_archived: showArchived || undefined,
      category: categoryFilter === CATEGORY_ANY ? undefined : categoryFilter,
    } }).then((r) => r.data),
  });

  const { data: catOptions = [] } = useQuery({
    queryKey: ["product-category-options"],
    queryFn: () => api.get("/products/category-options").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        category: form.category === CATEGORY_NONE ? "" : form.category,
        unit_price: parseFloat(form.unit_price) || 0,
        cost_price: form.cost_price !== "" ? parseFloat(form.cost_price) : null,
      };
      return editingId
        ? api.patch(`/products/${editingId}`, body).then((r) => r.data)
        : api.post("/products", body).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(editingId ? "Product saved" : "Product added");
      setOpenEdit(false);
      setEditingId(null);
      setForm(emptyForm);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const archiveMut = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => {
      toast.success("Archived");
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
  const unarchiveMut = useMutation({
    mutationFn: (id) => api.patch(`/products/${id}`, { is_archived: false }),
    onSuccess: () => {
      toast.success("Restored");
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const autoPreviewMut = useMutation({
    mutationFn: (only_blanks) => api.post(
      `/products/bulk-categorize?only_blanks=${only_blanks ? "true" : "false"}&dry_run=true`
    ).then((r) => r.data),
    onSuccess: (data) => { setAutoPreview(data); setAutoOpen(true); },
    onError: (e) => toast.error(e.response?.data?.detail || "Preview failed"),
  });
  const autoApplyMut = useMutation({
    mutationFn: (only_blanks) => api.post(
      `/products/bulk-categorize?only_blanks=${only_blanks ? "true" : "false"}&dry_run=false`
    ).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Categorized ${data.updated} product${data.updated === 1 ? "" : "s"}`);
      setAutoOpen(false);
      setAutoPreview(null);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Apply failed"),
  });

  const importMut = useMutation({
    mutationFn: async ({ file, dry_run }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dry_run ? "true" : "false");
      return api.post("/products/import-costs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((r) => r.data);
    },
    onSuccess: (data, vars) => {
      setImportPreview({ ...data, _file: vars.file });
      if (!vars.dry_run) {
        toast.success(`Updated cost on ${data.updated} product${data.updated === 1 ? "" : "s"}`);
        setImportOpen(false);
        setImportPreview(null);
        qc.invalidateQueries({ queryKey: ["products"] });
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Import failed"),
  });

  const doExport = async () => {
    try {
      const res = await api.get("/products/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpenEdit(true); };
  const openExisting = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      unit_price: p.unit_price ?? "",
      cost_price: p.cost_price ?? "",
      category: p.category || "",
      sku: p.sku || "",
      include_gst: p.include_gst !== false,
      include_service_fee: p.include_service_fee !== false,
    });
    setOpenEdit(true);
  };

  const activeCount = products.filter((p) => !p.is_archived).length;
  const uncategorizedCount = products.filter((p) => !p.is_archived && !p.category).length;
  const withCostCount = products.filter((p) => !p.is_archived && p.cost_price != null).length;

  return (
    <div>
      <PageHeader
        overline="Catalog"
        title="Products"
        subtitle="Reusable line items for quotations and invoices. Set a default GST + service-fee posture per product."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => autoPreviewMut.mutate(true)} data-testid="products-auto-categorize" disabled={autoPreviewMut.isPending}>
              <Wand2 className="h-4 w-4 mr-2" /> Auto-categorize
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={doExport} data-testid="products-export-csv">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => { setImportPreview(null); setImportOpen(true); }} data-testid="products-import-costs">
              <Upload className="h-4 w-4 mr-2" /> Import costs
            </Button>
            <Button className="rounded-sm bg-primary" onClick={openNew} data-testid="products-new">
              <Plus className="h-4 w-4 mr-2" /> New product
            </Button>
          </div>
        }
      />
      <div className="px-6 lg:px-10 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TotalCard label="Products" value={activeCount} tone="primary" />
          <TotalCard label="Categories" value={new Set(products.filter((p) => !p.is_archived).map((p) => p.category).filter(Boolean)).size} tone="neutral" />
          <TotalCard label="With cost" value={`${withCostCount} / ${activeCount}`} tone="green" data-testid="products-with-cost" />
          <TotalCard label="Uncategorized" value={uncategorizedCount} tone={uncategorizedCount > 0 ? "warn" : "neutral"} data-testid="products-uncategorized" />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, description or category…" className="pl-9 rounded-sm bg-secondary/40" data-testid="products-search" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] rounded-sm bg-secondary/40" data-testid="products-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CATEGORY_ANY}>All categories</SelectItem>
              {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-sm px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="products-show-archived" />
            Show archived
          </label>
        </div>

        <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="products-list">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_100px] gap-3 px-4 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div>Name</div><div>Category</div><div>Unit price</div><div>Cost</div><div>Flags</div><div />
          </div>
          {products.length === 0 && (
            <div className="p-12 text-center">
              <Package className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">No products yet.</div>
              <Button onClick={openNew} className="rounded-sm mt-4 bg-primary" data-testid="products-empty-new">Add your first product</Button>
            </div>
          )}
          {products.map((p) => (
            <div key={p.id} className={`grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_100px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center ${p.is_archived ? "opacity-60" : ""}`} data-testid={`product-row-${p.id}`}>
              <div>
                <div className="text-sm font-semibold">{p.name}{p.is_archived && <Badge className="ml-2 text-[9px] rounded-sm">Archived</Badge>}</div>
                {p.description && <div className="text-[10px] text-muted-foreground line-clamp-1">{p.description}</div>}
              </div>
              <div className="text-xs text-muted-foreground">
                {p.category ? <span className="rounded-sm border border-border px-1.5 py-0.5"><Tag className="h-2.5 w-2.5 mr-1 inline" />{p.category}</span> : <span className="text-[10px] uppercase tracking-widest text-amber-500/80">Uncategorized</span>}
              </div>
              <div className="text-sm font-semibold">{fmt(p.unit_price)}</div>
              <div className="text-xs text-muted-foreground">{p.cost_price != null ? fmt(p.cost_price) : "—"}</div>
              <div className="flex gap-1 text-[9px] uppercase tracking-widest">
                {p.include_gst && <Badge variant="outline" className="rounded-sm">GST</Badge>}
                {p.include_service_fee && <Badge variant="outline" className="rounded-sm text-primary border-primary/40">Fee</Badge>}
              </div>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => openExisting(p)} className="p-1 text-muted-foreground hover:text-primary" title="Edit" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                {p.is_archived
                  ? <button onClick={() => unarchiveMut.mutate(p.id)} className="p-1 text-muted-foreground hover:text-green-400" title="Restore" data-testid={`product-restore-${p.id}`}><TrendingUp className="h-4 w-4" /></button>
                  : <button onClick={() => archiveMut.mutate(p.id)} className="p-1 text-muted-foreground hover:text-red-400" title="Archive" data-testid={`product-archive-${p.id}`}><Archive className="h-4 w-4" /></button>
                }
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={openEdit} onOpenChange={(v) => { setOpenEdit(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div><Label>Description</Label><Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit price (SGD)</Label><Input type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="product-price" /></div>
              <div><Label>Cost price <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label><Input type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="product-cost" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category || CATEGORY_NONE} onValueChange={(v) => setForm({ ...form, category: v === CATEGORY_NONE ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="product-category"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CATEGORY_NONE}>— Uncategorized —</SelectItem>
                    {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>SKU <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label><Input className="rounded-sm mt-1.5 bg-secondary/40 uppercase" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} data-testid="product-sku" /></div>
              <div className="col-span-2 flex items-center gap-2"><Checkbox checked={form.include_gst} onCheckedChange={(v) => setForm({ ...form, include_gst: !!v })} data-testid="product-gst-toggle" /><Label className="text-xs">Apply GST by default when added to a document</Label></div>
              <div className="col-span-2 flex items-center gap-2"><Checkbox checked={form.include_service_fee} onCheckedChange={(v) => setForm({ ...form, include_service_fee: !!v })} data-testid="product-fee-toggle" /><Label className="text-xs">Include in service fee calculation by default</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={!form.name || !form.unit_price || saveMut.isPending} data-testid="product-save">
              {saveMut.isPending ? "Saving…" : editingId ? "Save changes" : "Add product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-categorize preview */}
      <Dialog open={autoOpen} onOpenChange={(v) => { setAutoOpen(v); if (!v) setAutoPreview(null); }}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="auto-categorize-dialog">
          <DialogHeader><DialogTitle>Auto-categorize preview</DialogTitle></DialogHeader>
          {autoPreview && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                {autoPreview.total === 0
                  ? "Everything already looks categorized — nothing to change."
                  : <>We'll tag <span className="font-semibold text-foreground">{autoPreview.total}</span> product{autoPreview.total === 1 ? "" : "s"} using keyword rules. Anything we can't confidently place drops into <span className="font-semibold">Other</span>.</>}
              </div>
              <div className="max-h-[360px] overflow-y-auto border border-border rounded-sm">
                <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold sticky top-0">
                  <div>Product</div><div>Currently</div><div>Will become</div>
                </div>
                {autoPreview.preview.map((row) => (
                  <div key={row.id} className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-2 border-b border-border last:border-b-0 text-xs">
                    <div className="truncate" title={row.name}>{row.name}</div>
                    <div className="text-muted-foreground">{row.before || <span className="text-amber-500/80">—</span>}</div>
                    <div className="text-primary font-semibold">{row.after}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAutoOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-primary" onClick={() => autoApplyMut.mutate(true)} disabled={autoApplyMut.isPending || !autoPreview || autoPreview.total === 0} data-testid="auto-categorize-apply">
              {autoApplyMut.isPending ? "Applying…" : `Apply to ${autoPreview?.total || 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import costs dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportPreview(null); }}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="import-costs-dialog">
          <DialogHeader><DialogTitle>Import cost prices</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Upload a CSV with columns <code className="text-foreground">sku</code>, <code className="text-foreground">name</code>, and <code className="text-foreground">cost_price</code>. We match by SKU first, then case-insensitive product name. Tip: click <strong>Export CSV</strong> first to get a pre-filled template.
            </div>
            {!importPreview && (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="text-xs"
                  data-testid="import-costs-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) importMut.mutate({ file: f, dry_run: true });
                  }}
                />
                {importMut.isPending && <div className="text-xs text-muted-foreground mt-2">Analyzing CSV…</div>}
              </div>
            )}
            {importPreview && (
              <div className="space-y-2" data-testid="import-costs-preview">
                <div className="flex gap-4 text-xs">
                  <div><span className="text-primary font-bold text-lg">{importPreview.matched_count}</span> matched</div>
                  <div><span className="text-amber-500 font-bold text-lg">{importPreview.unmatched_count}</span> unmatched</div>
                </div>
                {importPreview.matched?.length > 0 && (
                  <div className="max-h-[220px] overflow-y-auto border border-border rounded-sm">
                    <div className="grid grid-cols-[2fr_1fr_1fr_60px] gap-2 px-3 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold sticky top-0">
                      <div>Product</div><div>Old cost</div><div>New cost</div><div>Via</div>
                    </div>
                    {importPreview.matched.map((row) => (
                      <div key={row.id} className="grid grid-cols-[2fr_1fr_1fr_60px] gap-2 px-3 py-2 border-b border-border last:border-b-0 text-xs">
                        <div className="truncate" title={row.name}>{row.name}</div>
                        <div className="text-muted-foreground">{row.before != null ? fmt(row.before) : "—"}</div>
                        <div className="text-primary font-semibold">{fmt(row.after)}</div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{row.matched_by}</div>
                      </div>
                    ))}
                  </div>
                )}
                {importPreview.unmatched?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-amber-500/90 py-1">Show {importPreview.unmatched.length} unmatched row{importPreview.unmatched.length === 1 ? "" : "s"}</summary>
                    <div className="max-h-[160px] overflow-y-auto border border-border rounded-sm mt-1">
                      {importPreview.unmatched.map((row, i) => (
                        <div key={i} className="grid grid-cols-[1fr_2fr_1fr_1fr] gap-2 px-3 py-1.5 border-b border-border last:border-b-0">
                          <div className="text-muted-foreground">{row.sku || "—"}</div>
                          <div className="truncate" title={row.name}>{row.name || "—"}</div>
                          <div>{row.cost_price}</div>
                          <div className="text-amber-500/80">{row.reason}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setImportPreview(null); if (fileRef.current) fileRef.current.value = ""; }}>
              {importPreview ? "Choose different file" : "Cancel"}
            </Button>
            <Button
              className="rounded-sm bg-primary"
              disabled={!importPreview || importPreview.matched_count === 0 || importMut.isPending}
              onClick={() => importPreview?._file && importMut.mutate({ file: importPreview._file, dry_run: false })}
              data-testid="import-costs-commit"
            >
              {importMut.isPending ? "Updating…" : `Update ${importPreview?.matched_count || 0} cost${importPreview?.matched_count === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalCard({ label, value, tone = "neutral", ...rest }) {
  const cls = {
    neutral: "text-foreground",
    primary: "text-primary",
    green: "text-green-400",
    warn: "text-amber-500",
  }[tone];
  return (
    <Card className="rounded-sm bg-card border-border p-4" {...rest}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${cls}`}>{value}</div>
    </Card>
  );
}
